import TicketSuggestionGroup from './ticket-suggestion-group';
import { ProductType } from '../../../../models/product-type.enum';
import { TicketNameHelper } from '../../../../helpers/ticket-name.helper';
import { DateHelper } from '../../../../helpers/date.helper';
import { type ITicketSuggestion } from '../../../../models/ticket-suggestion.model';
import { TimeValidityType } from '../../../../models/time-validity-type.enum';
import { TimeValidityUnit } from '../../../../models/time-validity-unit.enum';

export default class GroupedTicketSuggestions {
  public singleTickets: TicketSuggestionGroup[];
  public prepaidTickets: TicketSuggestionGroup[];
  public periodTickets: TicketSuggestionGroup[];
  public dayTickets: TicketSuggestionGroup[];
  public flexTickets: TicketSuggestionGroup[];
  public partialYearTickets: TicketSuggestionGroup[];

  constructor() {
    this.singleTickets = [];
    this.prepaidTickets = [];
    this.periodTickets = [];
    this.dayTickets = [];
    this.flexTickets = [];
    this.partialYearTickets = [];
  }

  public addTicket(ticketSuggestion: ITicketSuggestion) {
    let ticketPriceSuggestions: TicketSuggestionGroup[];

    if (ticketSuggestion.productType === ProductType.singleTicket) {
      ticketPriceSuggestions = this.singleTickets;
      this.addSingleTicketSuggestion(ticketSuggestion, ticketPriceSuggestions, ProductType.singleTicket);
      return;
    }
    if (ticketSuggestion.productType === ProductType.periodTicket) {
      ticketPriceSuggestions = this.periodTickets;
    } else if (ticketSuggestion.productType === ProductType.prepaidTicket) {
      ticketPriceSuggestions = this.prepaidTickets;
    } else if (ticketSuggestion.productType === ProductType.dayTicket) {
      ticketPriceSuggestions = this.dayTickets;
    } else if (ticketSuggestion.productType === ProductType.flexTicket) {
      ticketPriceSuggestions = this.flexTickets;
    } else if (ticketSuggestion.productType === ProductType.partialYearTicket) {
      ticketPriceSuggestions = this.partialYearTickets;
    } else {
      return;
    }

    const name = TicketNameHelper.removeTravellerCategory(ticketSuggestion.productName);
    const existingTicketGroup = ticketPriceSuggestions.filter((x) => x.name === name);

    if (existingTicketGroup.length > 0) {
      existingTicketGroup[0].addPriceAndValidity(ticketSuggestion, name);
    } else {
      const ticketGroup = new TicketSuggestionGroup(ticketSuggestion.productType);
      ticketGroup.addPriceAndValidity(ticketSuggestion, name);

      if (ticketSuggestion.productType === ProductType.prepaidTicket) {
        ticketGroup.validityTimeMinutes = this.getValidityTimeInMinutes(ticketSuggestion);
      }
      ticketPriceSuggestions.push(ticketGroup);
    }
  }

  public hasTickets(): boolean {
    return this.singleTickets.length > 0 || this.prepaidTickets.length > 0 || this.periodTickets.length > 0 || this.dayTickets.length > 0;
  }

  private addSingleTicketSuggestion(ticketSuggestion: ITicketSuggestion, ticketPriceSuggestions: TicketSuggestionGroup[], productType: ProductType) {
    const validity = this.getValidityTimeInMinutes(ticketSuggestion);
    if (!validity) {
      return;
    }
    const ticketGroup = ticketPriceSuggestions.filter((x) => x.productType === productType);
    if (ticketGroup.length > 0) {
      ticketGroup[0].addSingleTicketPrice(ticketSuggestion, validity);
    } else {
      const newTicketGroup = new TicketSuggestionGroup(ticketSuggestion.productType);
      newTicketGroup.addSingleTicketPrice(ticketSuggestion, validity);
      ticketPriceSuggestions.push(newTicketGroup);
    }
  }

  private getValidityTimeInMinutes(ticketSuggestion: ITicketSuggestion): number | undefined {
    let validityTimeMinutes: number | undefined;
    if (ticketSuggestion.timeValidity?.type === TimeValidityType.amountUnit) {
      if (ticketSuggestion.timeValidity.unit === TimeValidityUnit.minutes) {
        validityTimeMinutes = ticketSuggestion.timeValidity.amount;
      } else if (ticketSuggestion.timeValidity.unit === TimeValidityUnit.hours) {
        validityTimeMinutes = ticketSuggestion.timeValidity.amount! * 60;
      } else if (ticketSuggestion.timeValidity.unit === TimeValidityUnit.days) {
        validityTimeMinutes = ticketSuggestion.timeValidity.amount! * 60 * 24;
      }
    } else if (ticketSuggestion.timeValidity?.type === TimeValidityType.fromToDateTime) {
      validityTimeMinutes = DateHelper.getDurationFromDateStrings(ticketSuggestion.timeValidity.fromDateTime!.toString(), ticketSuggestion.timeValidity.toDateTime!.toString());
    }
    return validityTimeMinutes;
  }
}
